/**
 * WishlistManager — handles localStorage persistence for wishlist items.
 */
class WishlistManager {
  static STORAGE_KEY = 'tickandpick_wishlist';

  /** @returns {Array<string | {id: string, [key: string]: string}>} */
  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  static hasItem(/** @type {string | number} */ productId) {
    const id = String(productId);
    return this.getItems().some((item) => String(typeof item === 'object' ? item.id : item) === id);
  }

  static toggle(
    /** @type {string | number} */ productId,
    /** @type {Record<string, string>} */ meta = {}
  ) {
    const items = this.getItems();
    const id = String(productId);
    const index = items.findIndex(
      (item) => String(typeof item === 'object' ? item.id : item) === id
    );
    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push({ id, ...meta });
    }
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    return index === -1; // true = added, false = removed
  }

  static getCount() {
    return this.getItems().length;
  }
}

/**
 * WishlistButton — custom element for the heart toggle button on product cards.
 */
class WishlistButton extends HTMLElement {
  static observedAttributes = ['data-active'];

  attributeChangedCallback(
    /** @type {string} */ name,
    /** @type {string|null} */ _oldValue,
    /** @type {string|null} */ newValue
  ) {
    if (name === 'data-active' && newValue === 'false') {
      const productId = this.dataset.productId;
      if (productId && WishlistManager.hasItem(productId)) {
        this.#updateState(true);
      }
    }
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    if (!productId) return;

    this.#updateState(WishlistManager.hasItem(productId));
    this.querySelector('button')?.addEventListener('click', this.#handleClick);
    window.addEventListener('wishlist:updated', this.#onWishlistUpdated);
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:updated', this.#onWishlistUpdated);
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    event.stopPropagation();

    const productId = this.dataset.productId;
    if (!productId) return;

    const meta = {
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      price: this.dataset.productPrice || '',
      image: this.dataset.productImage || '',
      url: this.dataset.productUrl || '',
    };

    WishlistManager.toggle(productId, meta);
    const isActive = WishlistManager.hasItem(productId);

    this.#updateState(isActive);

    const message = isActive
      ? (this.dataset.msgAdded || 'Added to wishlist')
      : (this.dataset.msgRemoved || 'Removed from wishlist');

    window.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: { productId, isActive, count: WishlistManager.getCount(), message },
      })
    );
  };

  #onWishlistUpdated = (/** @type {Event} */ event) => {
    const { productId, isActive } = /** @type {CustomEvent} */ (event).detail;
    if (String(productId) === String(this.dataset.productId)) {
      this.#updateState(isActive);
    }
  };

  #updateState(/** @type {boolean} */ isActive) {
    this.dataset.active = String(isActive);
    const button = this.querySelector('button');
    if (!button) return;
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute(
      'aria-label',
      isActive ? 'Remove from wishlist' : 'Add to wishlist'
    );
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * WishlistToast — singleton toast that listens for wishlist:updated events.
 */
class WishlistToast extends HTMLElement {
  #timer = /** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined);

  connectedCallback() {
    window.addEventListener('wishlist:updated', this.#onUpdated);
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:updated', this.#onUpdated);
  }

  #onUpdated = (/** @type {Event} */ event) => {
    const { message } = /** @type {CustomEvent} */ (event).detail;
    if (!message) return;
    this.#show(message);
  };

  #show(/** @type {string} */ message) {
    this.textContent = message;
    this.setAttribute('data-visible', 'true');

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.removeAttribute('data-visible');
    }, 3000);
  }
}

if (!customElements.get('wishlist-toast')) {
  customElements.define('wishlist-toast', WishlistToast);
}
